import { useState } from "react";
import { Chip, Menu, MenuItem, Box, CircularProgress } from "@mui/material";
import { KeyboardArrowDown as ArrowDownIcon } from "@mui/icons-material";
import { JobStatus, type JobResponse, UserRole } from "@taxassist/api-client";
import { useAuth } from "../../contexts/AuthContext";
import {
  getStatusDisplay,
  getStatusColor,
  type SystemStatus,
  type UserFriendlyStatus,
} from "../../utils/statusMapping";

// Color mapping for raw status display (ops context)
function getStatusColorForRaw(status: string): string {
  switch (status) {
    case JobStatus.TRIAGE:
    case JobStatus.VERIFICATION:
    case JobStatus.VERIFIED:
      return "#E1F5FE"; // Light blue for triage
    case JobStatus.NEW_FILES:
      return "#E0E0E0"; // Grey for new files
    case JobStatus.DRAFT:
      return "#E0E0E0"; // Grey
    case JobStatus.SUBMITTED:
    case JobStatus.PROCESSING:
    case JobStatus.DOWNLOAD_TO_PROCESS:
      return "#FFE0A5"; // Light orange
    case JobStatus.DONE:
      return "#D1F6C7"; // Light green
    case JobStatus.ERROR:
    case JobStatus.CANCELLED:
      return "#FFCDD2"; // Light red
    case JobStatus.ARCHIVED:
      return "#F5F5F5"; // Light grey for archived
    default:
      return "#E0E0E0"; // Default grey
  }
}

// Ops context status options with colors
const OPS_STATUS_OPTIONS = [
  {
    label: "READY TO SUBMIT",
    value: JobStatus.TRIAGE,
    backgroundColor: "#E1F5FE", // Blue
    textColor: "#1976D2", // Dark blue
  },
  {
    label: "SP PROCESSING",
    value: JobStatus.PROCESSING,
    backgroundColor: "#FFE0A5", // Yellow-orange
    textColor: "#8F5300", // Dark orange
  },
  {
    label: "DOWNLOAD TO PROCESS",
    value: JobStatus.DOWNLOAD_TO_PROCESS,
    backgroundColor: "#FFE0A5", // Yellow-orange
    textColor: "#8F5300", // Dark orange
  },
  {
    label: "IN VERIFICATION",
    value: JobStatus.VERIFIED,
    backgroundColor: "#E1F5FE", // Blue
    textColor: "#1976D2", // Dark blue
  },
  {
    label: "DOCUMENTS READY",
    value: JobStatus.DONE,
    backgroundColor: "#D1F6C7", // Green
    textColor: "#2F5B3A", // Dark green
  },
] as const;

// Map JobStatus to display label for ops context
function getOpsStatusLabel(status: JobStatus, job?: JobResponse): string {
  const txSoftwareName = ((job as any)?.taxSoftwareName || "").toLowerCase().replace(/\s+/g, "");
  const isUltraTax = txSoftwareName.includes("ultratax");

  // Fallback for statuses not in the main options
  switch (status) {
    case JobStatus.TRIAGE:
      return "READY TO SUBMIT";
    case JobStatus.PROCESSING:
    case JobStatus.SUBMITTED:
      return isUltraTax ? "DOWNLOAD TO PROCESS" : "SP PROCESSING";
    case JobStatus.DOWNLOAD_TO_PROCESS:
      return "DOWNLOAD TO PROCESS";
    case JobStatus.VERIFICATION:
    case JobStatus.VERIFIED:
      return "IN VERIFICATION";
    case JobStatus.DONE:
      return "DOCUMENTS READY";
    default:
      return status;
  }
}

interface StatusBadgeProps {
  status: JobStatus;
  size?: "small" | "medium";
  statusMapping?: ((status: SystemStatus) => string | null) | null; // Custom mapping function, null for raw status
  job?: JobResponse; // Optional job object (not currently used, kept for backward compatibility)
  context?: "client" | "ops"; // New prop to determine if dropdown should be shown
  onStatusChange?: (newStatus: JobStatus) => Promise<void>; // Callback for status changes in ops context
}

export function StatusBadge({
  status,
  size = "small",
  statusMapping = getStatusDisplay,
  job: _job,
  context = "client",
  onStatusChange,
}: StatusBadgeProps) {
  const { user } = useAuth();
  const isInternalUser = user?.role === UserRole.admin || user?.role === UserRole.reviewer;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const isMenuOpen = Boolean(anchorEl);

  // Show technical status for internal users (Admins/Reviewers) or in Ops context
  const showTechnicalStatus = isInternalUser || context === "ops";

  // Use custom mapping if provided, null for raw status, default to standard mapping
  const displayStatus =
    statusMapping === null ? status : statusMapping(status as SystemStatus);

  // For internal users or ops context, use the ops-specific labels
  const finalDisplayStatus =
    showTechnicalStatus ? getOpsStatusLabel(status, _job) : displayStatus;

  // Use raw colors for technical status or when status mapping is explicitly null
  const statusColor =
    statusMapping === null || showTechnicalStatus
      ? getStatusColorForRaw(status)
      : getStatusColor((displayStatus as UserFriendlyStatus) || "In Progress");

  // Define text colors for each status to match Queue_List
  const getTextColor = (status: string | null, isRaw: boolean = false) => {
    if (isRaw) {
      // Text colors for raw status display
      switch (status) {
        case JobStatus.TRIAGE:
        case JobStatus.VERIFICATION:
        case JobStatus.VERIFIED:
          return "#1976D2"; // Blue text for triage
        case JobStatus.NEW_FILES:
          return "#474747"; // Dark grey text for new files
        case JobStatus.DONE:
          return "#2F5B3A"; // Dark green text
        case JobStatus.ERROR:
        case JobStatus.CANCELLED:
          return "#D32F2F"; // Red text
        case JobStatus.SUBMITTED:
        case JobStatus.PROCESSING:
        case JobStatus.DOWNLOAD_TO_PROCESS:
        case JobStatus.DRAFT:
        case JobStatus.ARCHIVED:
        default:
          return "#474747"; // Dark grey text
      }
    } else {
      // Text colors for mapped status display
      switch (status) {
        case "Done":
          return "#2F5B3A"; // Dark green text from Figma
        case "In Progress":
        case "Download to Process":
          return "#8F5300"; // Dark orange text from Figma
        case "Unsubmitted":
        case "Queued":
        case "New Files":
          return "#474747"; // Dark grey text
        default:
          return "#474747"; // Dark grey text
      }
    }
  };

  const textColor = getTextColor(
    context === "ops" ? status : displayStatus,
    statusMapping === null || context === "ops",
  );

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (context === "ops" && onStatusChange) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusSelect = async (newStatus: JobStatus) => {
    if (newStatus === status || !onStatusChange) {
      handleClose();
      return;
    }

    setIsUpdating(true);
    try {
      await onStatusChange(newStatus);
    } finally {
      setIsUpdating(false);
      handleClose();
    }
  };

  // For ops context, make it clickable
  const chipSx = {
    fontWeight: 500,
    textTransform: "uppercase",
    minWidth: 100,
    backgroundColor: statusColor,
    color: textColor,
    border: "none",
    ...(context === "ops" && onStatusChange
      ? {
        cursor: "pointer",
        "&:hover": {
          filter: "brightness(0.95)",
        },
      }
      : {}),
    "& .MuiChip-label": {
      color: textColor,
      px: 1.5,
      display: "flex",
      alignItems: "center",
      gap: 0.5,
    },
  };

  // Custom label with arrow inside for ops context
  const chipLabel = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {finalDisplayStatus || "In Progress"}
      {context === "ops" && onStatusChange && !isUpdating && (
        <ArrowDownIcon sx={{ fontSize: 16, ml: 0.5 }} />
      )}
    </Box>
  );

  // Filter status options based on context and firm configuration
  const statusOptions = OPS_STATUS_OPTIONS.filter((option) => {
    const txSoftwareName = ((_job as any)?.taxSoftwareName || "").toLowerCase().replace(/\s+/g, "");
    const isUltraTax = txSoftwareName.includes("ultratax") || (status as any) === JobStatus.DOWNLOAD_TO_PROCESS;

    // For UltraTax firms:
    if (isUltraTax) {
      // Hide SP PROCESSING (PROCESSING) - we use DOWNLOAD_TO_PROCESS instead
      if (option.value === JobStatus.PROCESSING) return false;
      return true;
    }

    // For non-UltraTax firms:
    // Hide DOWNLOAD_TO_PROCESS - it only applies to UltraTax automated downloads
    if (option.value === JobStatus.DOWNLOAD_TO_PROCESS) {
      // Show it ONLY if it's the current status (shouldn't happen for non-UT, but for safety)
      return status === JobStatus.DOWNLOAD_TO_PROCESS;
    }

    return true;
  });

  return (
    <>
      <Chip
        label={chipLabel}
        size={size}
        sx={chipSx}
        onClick={handleClick}
        icon={
          isUpdating ? (
            <CircularProgress size={16} sx={{ color: textColor }} />
          ) : undefined
        }
      />

      {/* Dropdown menu for ops context */}
      {context === "ops" && onStatusChange && (
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              mt: 0.5,
            },
          }}
        >
          {statusOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleStatusSelect(option.value as JobStatus)}
              selected={status === option.value}
              sx={{
                minWidth: 220,
                py: 1,
              }}
            >
              <Chip
                label={option.label} // Use the original option label ("SP PROCESSING" or "DOWNLOAD TO PROCESS")
                size="small"
                sx={{
                  fontWeight: 500,
                  textTransform: "uppercase",
                  width: "100%",
                  backgroundColor: option.backgroundColor,
                  color: option.textColor,
                  border:
                    status === option.value
                      ? `2px solid ${option.textColor}`
                      : "none",
                  "& .MuiChip-label": {
                    color: option.textColor,
                    px: 1.5,
                  },
                }}
              />
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}
